import { useEffect, useState } from "react";
import { X } from "lucide-react";
import Logo from "./Logo";

const STORAGE_KEY = "discover_welcome_seen_v1";

/**
 * Plain controlled overlay (no Radix Dialog) so we never lock body
 * pointer-events / scroll. The previous Radix-based implementation
 * occasionally left `data-scroll-locked` / `pointer-events:none` on
 * the body at first mount, blocking the very first user click.
 */
export default function WelcomeDialog({ forceOpen = false, onClose }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (forceOpen) {
            setOpen(true);
            return;
        }
        const seen = localStorage.getItem(STORAGE_KEY);
        if (!seen) setOpen(true);
    }, [forceOpen]);

    // Defensive: whenever the dialog mounts/opens, make sure body is
    // never stuck with scroll-lock or pointer-events:none from a
    // previous Radix dialog instance.
    useEffect(() => {
        if (!open) return;
        try {
            document.body.style.pointerEvents = "";
            document.body.removeAttribute("data-scroll-locked");
            document.documentElement.style.pointerEvents = "";
        } catch { /* ignore */ }
    }, [open]);

    const handleClose = () => {
        try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
        setOpen(false);
        try {
            document.body.style.pointerEvents = "";
            document.body.removeAttribute("data-scroll-locked");
        } catch { /* ignore */ }
        onClose?.();
    };

    if (!open) return null;

    return (
        <div
            data-testid="welcome-dialog"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ pointerEvents: "auto" }}>
            {/* Backdrop — clicking does NOT close (welcome must be acknowledged) */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                aria-hidden="true"
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="welcome-title"
                className="relative rounded-3xl max-w-lg w-full bg-background shadow-2xl overflow-hidden border-0 max-h-[90vh] flex flex-col">
                <button
                    type="button"
                    onClick={handleClose}
                    aria-label="Fermer"
                    data-testid="welcome-close-btn"
                    className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-foreground shadow-md flex items-center justify-center">
                    <X size={18} />
                </button>
                <div className="bg-discover-gradient text-white p-6 text-center shrink-0">
                    <div className="flex justify-center mb-3"><Logo size={48} /></div>
                    <h2 id="welcome-title" className="text-2xl font-black">
                        Bienvenue sur Discoveur !
                    </h2>
                    <p className="text-sm opacity-95 mt-1">
                        Bienvenue dans la communauté discoveuriste 👋
                    </p>
                </div>
                <div className="p-6 space-y-4 text-sm leading-relaxed overflow-y-auto">
                    <p>
                        Discoveur t'aide à <b>découvrir, choisir, organiser et partager</b>
                        des moments autour d'événements, à ton rythme.
                    </p>
                    <div>
                        <p className="font-bold mb-1">👉 Ici, tu peux :</p>
                        <ul className="space-y-1 ml-1">
                            <li>• parcourir librement tous les événements,</li>
                            <li>• suivre <span className="text-amber-500">⭐</span> ceux qui t'intéressent sans t'engager,</li>
                            <li>• rejoindre <span className="text-emerald-500">✅</span> un événement quand tu es sûr(e),</li>
                            <li>• ajouter un événement à ton agenda avec un rappel.</li>
                        </ul>
                    </div>
                    <div className="rounded-2xl bg-accent/50 p-3">
                        <p className="font-bold mb-1">🔒 Ton profil est invisible par défaut.</p>
                        <p className="mb-2">Tu gardes toujours le contrôle :</p>
                        <ul className="space-y-1 ml-1 text-xs">
                            <li>• tu choisis si tu souhaites échanger avec d'autres participants,</li>
                            <li>• et uniquement lorsque tu rejoins un événement.</li>
                        </ul>
                    </div>
                    <p>
                        🙏 Bonne exploration, et n'hésite pas à nous faire tes retours pour
                        nous aider à améliorer Discoveur.
                    </p>
                    <p className="text-center font-bold text-lg discover-gradient-text">
                        Bonne découverte ✨
                    </p>
                    <button
                        type="button"
                        onClick={handleClose}
                        data-testid="welcome-cta-btn"
                        className="w-full rounded-full bg-discover-gradient text-white text-base font-semibold py-3">
                        C'est parti !
                    </button>
                </div>
            </div>
        </div>
    );
}
